import { AxiosResponse } from 'axios';
import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';
import {
  ResponseApiUser,
  ResponseApiAllUsers,
  InviteUser,
  InviteUserResponse,
  EditUserOptions,
  User,
} from '@/types/api';

const getUser = async (): Promise<AxiosResponse<ResponseApiUser>> => {
  const {
    data: { user: authUser },
  } = await supabase.auth.getUser();
  if (!authUser) throw new Error('Not authenticated');

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('auth_user_id', authUser.id)
    .single();

  if (error) throw error;

  return asResponse({
    id: profile.id,
    auth0Id: null,
    email: profile.email,
    name: `${profile.first_name} ${profile.last_name}`,
    personId: profile.id,
    person: null,
    roles: [],
    slackId: profile.slack_id,
    createdAt: profile.created_at,
    updatedAt: profile.updated_at,
    deletedAt: profile.deleted_at,
  } as any);
};

export type GetAllUsersOptions = {
  term?: string;
  page?: number;
  take?: number;
};

const getAllUsers = async (
  options?: GetAllUsersOptions
): Promise<AxiosResponse<ResponseApiAllUsers>> => {
  const page = options?.page || 1;
  const take = options?.take || 20;
  const [from, to] = paginationRange(page, take);

  const orgId = getActiveOrgId();
  let query = supabase
    .from('profiles')
    .select('*', { count: 'exact' })
    .not('auth_user_id', 'is', null)
    .is('deleted_at', null)
    .range(from, to)
    .order('first_name');

  if (orgId) {
    query = query.eq('organization_id', orgId);
  }

  if (options?.term) {
    query = query.or(
      `first_name.ilike.%${options.term}%,last_name.ilike.%${options.term}%,email.ilike.%${options.term}%`
    );
  }

  const { data: profiles, count, error } = await query;
  if (error) throw error;

  // Fetch roles for all returned profiles
  const profileIds = (profiles || []).map((p: any) => p.id);
  const { data: profileRoles } =
    profileIds.length > 0
      ? await supabase
          .from('profile_roles')
          .select('profile_id, roles(id, value, role_permissions(permissions(id, value)))')
          .in('profile_id', profileIds)
      : { data: [] };

  // Build a map of profile_id → Role[]
  const rolesMap = new Map<string, any[]>();
  for (const pr of profileRoles ?? []) {
    const p = pr as any;
    if (!rolesMap.has(p.profile_id)) rolesMap.set(p.profile_id, []);
    rolesMap.get(p.profile_id)?.push({
      id: p.roles.id,
      value: p.roles.value,
      permissions: (p.roles.role_permissions ?? []).map((rp: any) => ({
        id: rp.permissions.id,
        value: rp.permissions.value,
      })),
    });
  }

  const users = (profiles || []).map((p: any) => ({
    id: p.id,
    auth0Id: null,
    email: p.email,
    name: `${p.first_name} ${p.last_name}`,
    personId: p.id,
    person: null,
    roles: rolesMap.get(p.id) ?? [],
    slackId: p.slack_id,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    deletedAt: p.deleted_at,
    isOwner: p.is_owner ?? false,
  }));

  const totalCount = count || 0;

  return asResponse({
    users: buildPaginatedResponse(users, totalCount, page, take),
  } as any);
};

const resolveRoleIds = async (roleValues: string[]): Promise<string[]> => {
  if (roleValues.length === 0) return [];
  // Check if values are already UUIDs (legacy) or role value strings
  const isUuid = /^[0-9a-f]{8}-/.test(roleValues[0]);
  if (isUuid) return roleValues;

  const { data: roles } = await supabase.from('roles').select('id, value').in('value', roleValues);

  return (roles ?? []).map((r: any) => r.id);
};

const inviteUser = async (params: InviteUser): Promise<AxiosResponse<InviteUserResponse>> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: {
      name: params.name,
      email: params.email,
      roles: params.roles ?? [],
      userId: session?.user?.id,
      organizationId: getActiveOrgId() || undefined,
      ...(params.usePassword ? { action: 'createWithPassword', password: params.password } : {}),
    },
  });

  if (error) {
    // Extract the actual error message from the edge function response
    let message = error.message || 'Failed to invite user';
    try {
      const ctx = (error as any).context;
      if (ctx?.json) {
        const body = await ctx.json();
        if (body?.error) message = body.error;
      }
    } catch {
      // Use default message
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);

  return asResponse({
    user: {
      id: data.user.id,
      auth0Id: null,
      email: data.user.email,
      name: data.user.name,
      personId: data.user.id,
      roles: [],
      slackId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      deletedAt: null,
    },
  } as InviteUserResponse);
};

const resendInvite = async (email: string, name: string): Promise<{ tempPassword?: string }> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: {
      name,
      email,
      roles: [],
      userId: session?.user?.id,
      resend: true,
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return { tempPassword: data?.tempPassword };
};

const deleteUser = async (userId: string): Promise<AxiosResponse<string>> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: {
      action: 'delete',
      profileId: userId,
      userId: session?.user?.id,
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return asResponse('ok');
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const validateAccessToPage = async (_webArtifact: string): Promise<AxiosResponse<any>> => {
  // Access validation is now done client-side via useHasPermission
  return asResponse({ hasAccess: true });
};

const editUser = async ({ id, ...body }: EditUserOptions): Promise<AxiosResponse<User>> => {
  const updates: any = {};
  if (body.name) {
    const [first, ...rest] = body.name.split(' ');
    updates.first_name = first;
    updates.last_name = rest.join(' ');
  }
  if (body.email) updates.email = body.email;
  if (body.roles) {
    // Replace roles
    await supabase.from('profile_roles').delete().eq('profile_id', id);
    if (body.roles.length) {
      const roleIds = await resolveRoleIds(body.roles);
      if (roleIds.length > 0) {
        await supabase.from('profile_roles').insert(
          roleIds.map((roleId: string) => ({
            profile_id: id,
            role_id: roleId,
          }))
        );
      }
    }
  }

  if (Object.keys(updates).length) {
    const { error } = await supabase.from('profiles').update(updates).eq('id', id);
    if (error) throw error;
  }

  const { data: profile } = await supabase.from('profiles').select('*').eq('id', id).single();

  return asResponse({
    id: profile.id,
    auth0Id: null,
    email: profile.email,
    name: `${profile.first_name} ${profile.last_name}`,
    personId: profile.id,
    person: null,
    roles: [],
    slackId: profile.slack_id,
    createdAt: profile.created_at,
    updatedAt: profile.updated_at,
    deletedAt: profile.deleted_at,
  } as any);
};

export default {
  getUser,
  getAllUsers,
  inviteUser,
  resendInvite,
  deleteUser,
  validateAccessToPage,
  editUser,
};
